import { useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  Gender,
  MaritalStatus,
  PossibleDurationToStayInJapan,
  SpouseResponsibility,
} from '@/_gql/graphql'

import { queryAuthProfile } from '@/services/auth/query'
import { DURATION_STAY_OPTIONS } from '@/services/user/constant'
import { queryUserInformation } from '@/services/user/query'

import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { KeyValue } from '@/components/ui/key-value'

import { formatDateOnly, normalizeEnum } from '@/lib/utils/utils'

export const Route = createFileRoute('/dashboard/personal-information')({
  component: RouteComponent,
})

function RouteComponent() {
  const profileQry = useSuspenseQuery(queryAuthProfile())
  const personalQry = useSuspenseQuery(queryUserInformation())
  const { user } = profileQry.data.getProfile
  const { data: userPersonalInfo } = personalQry

  const age = useMemo(() => {
    if (!userPersonalInfo?.birthdate) return '-'
    const birthdate = new Date(userPersonalInfo?.birthdate)
    const today = new Date()
    const age = today.getFullYear() - birthdate.getFullYear()
    const m = today.getMonth() - birthdate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) {
      return age - 1
    } else {
      return age
    }
  }, [userPersonalInfo])

  return (
    <div className="rounded-lg border-t-4 border-yes-600 bg-background">
      <div className="border-b px-4 py-4">
        <h2 className="font-semibold">Informasi Pribadi</h2>
      </div>
      <div className="divide-y divide-gray-200 px-4">
        <div className="grid grid-cols-2 gap-4 py-4">
          <div>
            <Avatar className="size-40 rounded-sm">
              <AvatarImage
                src="/img/landing/logo.jpg"
                alt="Avatar"
                className="rounded-sm"
              />
            </Avatar>
          </div>
          <KeyValue
            label="Nama (Hiragana)"
            value={userPersonalInfo?.name_hiragana || '-'}
            className="col-span-full"
          />
          <KeyValue
            label="Nama Lengkap"
            value={userPersonalInfo?.name || '-'}
            className="col-span-full"
          />
          <KeyValue
            label="Jenis Kelamin"
            value={
              !userPersonalInfo?.gender
                ? '-'
                : userPersonalInfo?.gender.toUpperCase() === Gender.Men
                  ? 'Laki-laki'
                  : 'Perempuan'
            }
            className="col-span-full"
          />
          <KeyValue
            label="Tanggal Lahir"
            value={
              userPersonalInfo?.birthdate
                ? formatDateOnly(userPersonalInfo?.birthdate)
                : '-'
            }
          />
          <KeyValue label="Usia" value={age} />
        </div>
        <div className="grid grid-cols-2 gap-4 py-4">
          <KeyValue
            label="Alamat"
            value={[
              userPersonalInfo?.address,
              userPersonalInfo?.city?.name,
              userPersonalInfo?.province?.name,
              userPersonalInfo?.country?.name,
            ]
              .filter(Boolean)
              .join(', ')}
            className="col-span-full"
          />
          {/* <KeyValue
            label='Nomor Telepon'
            value={userPersonalInfo?}
          /> */}
          <KeyValue
            label="Email"
            value={user.email}
            className="col-span-full"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 py-4">
          <KeyValue
            label="Status Pernikahan"
            value={
              userPersonalInfo?.marital_status
                ? MARITAL[normalizeEnum(userPersonalInfo?.marital_status)]
                : '-'
            }
            className="col-span-full"
          />
          <KeyValue
            label="Tanggung Jawab terhadap Pasangan"
            value={
              userPersonalInfo?.spouse_responsibility
                ? SPOUSE_RESPONSIBILITY[
                    normalizeEnum(userPersonalInfo?.spouse_responsibility)
                  ]
                : '-'
            }
            className="col-span-full"
          />
          <KeyValue
            label="Jumlah Tanggungan (Tidak termasuk pasangan)"
            value={userPersonalInfo?.spouse_count || '-'}
            className="col-span-full"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 py-4">
          <KeyValue
            label="Promosi Diri (Jiko PR /自己PR) (Hiragana) "
            value={userPersonalInfo?.jiko_pr || '-'}
            className="col-span-full"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 py-4">
          <KeyValue
            label="Perkiraan lama tinggal di Jepang"
            value={
              DURATION_STAY_OPTIONS.find(
                (y) =>
                  y.value ===
                  normalizeEnum(
                    userPersonalInfo?.possible_duration_to_stay_in_japan
                  )?.toLowerCase()
              )?.label || '-'
            }
            className="col-span-full"
          />
        </div>
      </div>
    </div>
  )
}

const MARITAL = {
  [MaritalStatus.Single]: 'Belum Menikah',
  [MaritalStatus.Married]: 'Menikah',
  [MaritalStatus.Divorced]: 'Bercerai',
} satisfies Record<MaritalStatus, string>

const SPOUSE_RESPONSIBILITY = {
  [SpouseResponsibility.Yes]: 'Tidak',
  [SpouseResponsibility.No]: 'Ya',
}
